import { Renderer } from '@client/core/Renderer';
import { Player } from '@client/entities/Player';
import { Enemy, EnemyRarity } from '@client/entities/Enemy';
import { IsometricMap, TILE_COLORS, TileType } from '@client/world/IsometricMap';
import { PropRegistry, PropType } from '@client/assets/props';
import { ZONES, ZoneType } from '@shared/world/Zone';
import { t } from '@locales/index';

export interface MinimapConfig {
  size: number;          // Width/height in pixels
  scale: number;         // World units per minimap pixel
  x: number;             // Screen position X
  y: number;             // Screen position Y
}

export interface MinimapMarker {
  x: number;
  y: number;
  type: 'portal' | 'item' | 'quest' | 'npc' | 'waypoint';
  color?: string;
  label?: string;
  /** NPC quest marker: 'available' shows "!", 'completable' shows "?" */
  questMarker?: 'available' | 'completable' | 'none';
  /** Whether this waypoint has been activated */
  waypointActivated?: boolean;
}

export class Minimap {
  private config: MinimapConfig;
  private player: Player;
  private enemies: Enemy[] = [];
  private markers: MinimapMarker[] = [];
  private map: IsometricMap | null = null;

  // Expanded (centered) mode
  private isExpanded: boolean = false;
  private expandedSize: number = 600;
  private screenWidth: number = 1280;
  private screenHeight: number = 720;

  // Offscreen terrain cache (1 pixel per iso-tile)
  private terrainCache: HTMLCanvasElement | null = null;
  private terrainCacheId: string | null = null;

  // Cached background (shadow + rounded rect) for small minimap
  private bgCache: HTMLCanvasElement | null = null;
  private bgCacheSize: number = 0;

  constructor(player: Player, config?: Partial<MinimapConfig>) {
    this.player = player;
    this.config = {
      size: 150,
      scale: 5, // 5 world units per pixel
      x: 0,     // Will be set based on screen size
      y: 0,
      ...config,
    };
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    // Scale expanded size based on screen - 85% of smaller dimension
    this.expandedSize = Math.min(width, height) * 0.85;
  }

  setPosition(x: number, y: number): void {
    this.config.x = x;
    this.config.y = y;
  }

  setConfigSize(size: number): void {
    this.config.size = size;
  }

  setMap(map: IsometricMap): void {
    this.map = map;
    this.invalidateCache();
  }

  updateEnemies(enemies: Enemy[]): void {
    this.enemies = enemies;
  }

  setMarkers(markers: MinimapMarker[]): void {
    this.markers = markers;
  }

  addMarker(marker: MinimapMarker): void {
    this.markers.push(marker);
  }

  clearMarkers(): void {
    this.markers = [];
  }

  // Toggle expanded mode (Tab key)
  toggle(): void {
    this.isExpanded = !this.isExpanded;
  }

  setExpanded(expanded: boolean): void {
    this.isExpanded = expanded;
  }

  isExpandedMode(): boolean {
    return this.isExpanded;
  }

  close(): void {
    this.isExpanded = false;
  }

  private invalidateCache(): void {
    this.terrainCache = null;
    this.terrainCacheId = null;
  }

  /** Convert world position to isometric minimap coordinates (matches terrain cache) */
  private worldToIso(worldX: number, worldY: number): { ix: number; iy: number } {
    // worldX = (tx-ty)*32, worldY = (tx+ty)*16
    // isoX = tx-ty = worldX/32
    // isoY = (tx+ty)/2 = worldY/32
    return { ix: worldX / 32, iy: worldY / 32 };
  }

  /** Build (or return cached) offscreen canvas with 1px-per-tile terrain colors in isometric projection */
  private getTerrainCache(): HTMLCanvasElement | null {
    if (!this.map) return null;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const cacheId = `${this.map.getZoneId()}_${mapWidth}_${mapHeight}_iso`;

    if (this.terrainCache && this.terrainCacheId === cacheId) {
      return this.terrainCache;
    }

    // Isometric-projected dimensions
    // isoX range: -(mapHeight-1) to (mapWidth-1), total = mapWidth + mapHeight - 1
    // isoY range: 0 to (mapWidth+mapHeight-2)/2, total = ceil((mapWidth+mapHeight-1)/2)
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);

    // Create offscreen canvas
    const canvas = document.createElement('canvas');
    canvas.width = isoW;
    canvas.height = isoH;
    const ctx = canvas.getContext('2d')!;

    // Fill with dark background for VOID / out-of-bounds
    ctx.fillStyle = '#0a0a12';
    ctx.fillRect(0, 0, isoW, isoH);

    // Draw each tile in isometric projection
    for (let ty = 0; ty < mapHeight; ty++) {
      for (let tx = 0; tx < mapWidth; tx++) {
        const tile = this.map.getTile(tx, ty);
        if (!tile) continue;

        // Skip VOID tiles — background is already dark
        if (tile.type === TileType.VOID) continue;

        const colors = TILE_COLORS[tile.type];
        if (!colors) continue;

        // Use dark shade for non-walkable, base for walkable
        ctx.fillStyle = tile.walkable ? colors.base : colors.dark;
        const cx = tx - ty + (mapHeight - 1);
        const cy = Math.round((tx + ty) / 2);
        ctx.fillRect(cx, cy, 1, 1);
      }
    }

    // Draw non-walkable props as slightly darker spots
    const props = this.map.getProps();
    if (props) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      for (const prop of props) {
        const asset = PropRegistry.get(prop.type as PropType);
        if (!asset || asset.walkable) continue;
        const cx = Math.floor(prop.x) - Math.floor(prop.y) + (mapHeight - 1);
        const cy = Math.round((Math.floor(prop.x) + Math.floor(prop.y)) / 2);
        if (cx >= 0 && cx < isoW && cy >= 0 && cy < isoH) {
          ctx.fillRect(cx, cy, 1, 1);
        }
      }
    }

    this.terrainCache = canvas;
    this.terrainCacheId = cacheId;
    return canvas;
  }

  /** Build (or return cached) background canvas with shadow + rounded rect for small minimap */
  private getBackgroundCache(size: number, borderRadius: number): HTMLCanvasElement {
    if (this.bgCache && this.bgCacheSize === size) {
      return this.bgCache;
    }

    // Extra padding for shadow bleed
    const pad = 16;
    const canvas = document.createElement('canvas');
    canvas.width = size + pad * 2;
    canvas.height = size + pad * 2;
    const ctx = canvas.getContext('2d')!;

    // Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Rounded rect background
    ctx.beginPath();
    ctx.roundRect(pad, pad, size, size, borderRadius);
    ctx.fillStyle = 'rgba(10, 10, 18, 0.7)';
    ctx.fill();

    this.bgCache = canvas;
    this.bgCacheSize = size;
    return canvas;
  }

  private getZoneInfo(): { name: string; levelRange: string } | null {
    if (!this.map) return null;
    const zoneId = this.map.getZoneId();
    if (!zoneId) return null;
    const zone = ZONES[zoneId as ZoneType];
    if (!zone) return null;
    return {
      name: zone.name,
      levelRange: `Lv ${zone.levelRange.min}-${zone.levelRange.max}`,
    };
  }

  render(renderer: Renderer): void {
    if (this.isExpanded) {
      this.renderExpanded(renderer);
    } else {
      this.renderSmall(renderer);
    }
  }

  // ===================== SMALL MINIMAP =====================

  private renderSmall(renderer: Renderer): void {
    const ctx = renderer.context;
    const { x, y, size } = this.config;

    if (!this.map) return;

    const viewRadius = 25; // iso units to show around player
    const tileSize = size / (viewRadius * 2);
    const halfSize = size / 2;
    const borderRadius = 6;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const playerIso = this.worldToIso(this.player.position.x, this.player.position.y);

    // Isometric cache dimensions
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);

    // --- Background with rounded corners + shadow (cached) ---
    ctx.save();

    const bgCanvas = this.getBackgroundCache(size, borderRadius);
    const pad = 16;
    ctx.drawImage(bgCanvas, x - pad, y - pad);

    // Clip to rounded rect
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, borderRadius);
    ctx.clip();

    // --- Draw terrain from cache ---
    const terrainCanvas = this.getTerrainCache();
    if (terrainCanvas) {
      // Source rect in iso coordinates centered on player
      // playerIso.ix maps to cache x = playerIso.ix + (mapHeight - 1)
      const srcX = playerIso.ix + (mapHeight - 1) - viewRadius;
      const srcY = playerIso.iy - viewRadius;
      const srcW = viewRadius * 2;
      const srcH = viewRadius * 2;

      // Clamp source to valid range and adjust dest accordingly
      const clampedSrcX = Math.max(0, srcX);
      const clampedSrcY = Math.max(0, srcY);
      const clampedSrcEndX = Math.min(isoW, srcX + srcW);
      const clampedSrcEndY = Math.min(isoH, srcY + srcH);

      if (clampedSrcEndX > clampedSrcX && clampedSrcEndY > clampedSrcY) {
        const destX = x + (clampedSrcX - srcX) * tileSize;
        const destY = y + (clampedSrcY - srcY) * tileSize;
        const destW = (clampedSrcEndX - clampedSrcX) * tileSize;
        const destH = (clampedSrcEndY - clampedSrcY) * tileSize;

        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(
          terrainCanvas,
          clampedSrcX, clampedSrcY,
          clampedSrcEndX - clampedSrcX, clampedSrcEndY - clampedSrcY,
          destX, destY, destW, destH
        );
      }
    }

    // --- Zone boundary indicator with gradient fade edge ---
    // Boundary in minimap screen space using iso coordinates
    const boundaryLeft = x + halfSize + (-(mapHeight - 1) - playerIso.ix) * tileSize;
    const boundaryTop = y + halfSize + (0 - playerIso.iy) * tileSize;
    const boundaryWidth = isoW * tileSize;
    const boundaryHeight = isoH * tileSize;

    // Inner border glow (subtle gradient inset)
    const borderFade = 6;
    // Top edge fade
    if (boundaryTop > y && boundaryTop < y + size) {
      const topGrad = ctx.createLinearGradient(0, boundaryTop, 0, boundaryTop + borderFade);
      topGrad.addColorStop(0, 'rgba(180, 140, 80, 0.25)');
      topGrad.addColorStop(1, 'rgba(180, 140, 80, 0)');
      ctx.fillStyle = topGrad;
      ctx.fillRect(Math.max(boundaryLeft, x), boundaryTop, Math.min(boundaryWidth, size), borderFade);
    }
    // Bottom edge fade
    const boundaryBottom = boundaryTop + boundaryHeight;
    if (boundaryBottom > y && boundaryBottom < y + size) {
      const botGrad = ctx.createLinearGradient(0, boundaryBottom - borderFade, 0, boundaryBottom);
      botGrad.addColorStop(0, 'rgba(180, 140, 80, 0)');
      botGrad.addColorStop(1, 'rgba(180, 140, 80, 0.25)');
      ctx.fillStyle = botGrad;
      ctx.fillRect(Math.max(boundaryLeft, x), boundaryBottom - borderFade, Math.min(boundaryWidth, size), borderFade);
    }
    // Left edge fade
    if (boundaryLeft > x && boundaryLeft < x + size) {
      const leftGrad = ctx.createLinearGradient(boundaryLeft, 0, boundaryLeft + borderFade, 0);
      leftGrad.addColorStop(0, 'rgba(180, 140, 80, 0.25)');
      leftGrad.addColorStop(1, 'rgba(180, 140, 80, 0)');
      ctx.fillStyle = leftGrad;
      ctx.fillRect(boundaryLeft, Math.max(boundaryTop, y), borderFade, Math.min(boundaryHeight, size));
    }
    // Right edge fade
    const boundaryRight = boundaryLeft + boundaryWidth;
    if (boundaryRight > x && boundaryRight < x + size) {
      const rightGrad = ctx.createLinearGradient(boundaryRight - borderFade, 0, boundaryRight, 0);
      rightGrad.addColorStop(0, 'rgba(180, 140, 80, 0)');
      rightGrad.addColorStop(1, 'rgba(180, 140, 80, 0.25)');
      ctx.fillStyle = rightGrad;
      ctx.fillRect(boundaryRight - borderFade, Math.max(boundaryTop, y), borderFade, Math.min(boundaryHeight, size));
    }

    // Border line
    ctx.strokeStyle = 'rgba(180, 140, 80, 0.35)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(boundaryLeft, boundaryTop, boundaryWidth, boundaryHeight);

    // --- Visible iso range for dynamic elements ---
    const minIsoX = playerIso.ix - viewRadius;
    const maxIsoX = playerIso.ix + viewRadius;
    const minIsoY = playerIso.iy - viewRadius;
    const maxIsoY = playerIso.iy + viewRadius;

    // --- Enemies ---
    for (const enemy of this.enemies) {
      if (!enemy.isAlive) continue;

      const enemyIso = this.worldToIso(enemy.position.x, enemy.position.y);
      if (enemyIso.ix < minIsoX || enemyIso.ix > maxIsoX || enemyIso.iy < minIsoY || enemyIso.iy > maxIsoY) continue;

      const px = x + halfSize + (enemyIso.ix - playerIso.ix) * tileSize;
      const py = y + halfSize + (enemyIso.iy - playerIso.iy) * tileSize;

      const rarity = (enemy as Enemy & { rarity?: EnemyRarity }).rarity || 'normal';
      let dotColor: string;
      let dotSize: number;

      switch (rarity) {
        case 'boss':
          dotColor = '#ff4444';
          dotSize = 4;
          break;
        case 'elite':
          dotColor = '#ffaa44';
          dotSize = 3;
          break;
        default:
          dotColor = '#cc3333';
          dotSize = 2;
      }

      ctx.beginPath();
      ctx.arc(px, py, dotSize, 0, Math.PI * 2);
      ctx.fillStyle = dotColor;
      ctx.fill();
    }

    // --- Markers (NPC, portal, quest) ---
    this.renderSmallMarkers(ctx, x, y, halfSize, playerIso, tileSize, minIsoX, maxIsoX, minIsoY, maxIsoY);

    // --- Player indicator with direction arrow ---
    const centerX = x + halfSize;
    const centerY = y + halfSize;
    const time = Date.now() / 1000;

    // Pulse glow
    const pulseAlpha = 0.25 + Math.sin(time * 3) * 0.15;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 7, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(68, 170, 255, ${pulseAlpha})`;
    ctx.fill();

    // Direction arrow (small triangle)
    const dirAngle = this.player.getFacingAngle();
    const arrowDist = 7;
    const arrowSize = 3;
    const arrowTipX = centerX + Math.cos(dirAngle) * arrowDist;
    const arrowTipY = centerY + Math.sin(dirAngle) * arrowDist;
    const arrowLeftX = centerX + Math.cos(dirAngle + 2.5) * arrowSize;
    const arrowLeftY = centerY + Math.sin(dirAngle + 2.5) * arrowSize;
    const arrowRightX = centerX + Math.cos(dirAngle - 2.5) * arrowSize;
    const arrowRightY = centerY + Math.sin(dirAngle - 2.5) * arrowSize;

    ctx.beginPath();
    ctx.moveTo(arrowTipX, arrowTipY);
    ctx.lineTo(arrowLeftX, arrowLeftY);
    ctx.lineTo(arrowRightX, arrowRightY);
    ctx.closePath();
    ctx.fillStyle = '#88ccff';
    ctx.fill();

    // Player dot
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#44aaff';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore(); // end clipping

    // --- Border frame ---
    ctx.strokeStyle = '#2a2520';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, borderRadius);
    ctx.stroke();

    // Inner highlight (top-left edge)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x + 1, y + 1, size - 2, size - 2, borderRadius - 1);
    ctx.stroke();

    // --- Zone name label above minimap ---
    const zoneInfo = this.getZoneInfo();
    if (zoneInfo) {
      ctx.font = 'bold 9px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = 'rgba(200, 200, 200, 0.8)';
      ctx.fillText(zoneInfo.name, x + halfSize, y - 3);
    }
  }

  // ===================== EXPANDED MINIMAP =====================

  private renderExpanded(renderer: Renderer): void {
    const ctx = renderer.context;

    if (!this.map) return;

    const size = this.expandedSize;
    const x = (this.screenWidth - size) / 2;
    const y = (this.screenHeight - size) / 2;
    const borderRadius = 10;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);
    const tileSize = size / Math.max(isoW, isoH);
    const offsetX = (size - isoW * tileSize) / 2;
    const offsetY = (size - isoH * tileSize) / 2;

    // --- Full-screen dark overlay ---
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // --- Map panel background + shadow ---
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;

    ctx.beginPath();
    ctx.roundRect(x - 4, y - 4, size + 8, size + 8, borderRadius + 2);
    ctx.fillStyle = 'rgba(15, 12, 10, 0.9)';
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Clip to panel
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, borderRadius);
    ctx.clip();

    // Dark interior
    ctx.fillStyle = '#0a0a12';
    ctx.fillRect(x, y, size, size);

    // --- Draw terrain from cache ---
    const terrainCanvas = this.getTerrainCache();
    if (terrainCanvas) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        terrainCanvas,
        0, 0, isoW, isoH,
        x + offsetX, y + offsetY,
        isoW * tileSize, isoH * tileSize
      );
    }

    // --- Enemies ---
    this.renderExpandedEnemies(ctx, x, y, size);

    // --- Markers ---
    this.renderExpandedMarkers(ctx, x, y, size);

    // --- Player ---
    this.renderExpandedPlayer(ctx, x, y, size);

    ctx.restore(); // end clipping

    // --- Decorative border ---
    ctx.strokeStyle = '#3d3429';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, borderRadius);
    ctx.stroke();

    // Inner highlight
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x + 1.5, y + 1.5, size - 3, size - 3, borderRadius - 1);
    ctx.stroke();

    // --- Zone name + level range above map ---
    const zoneInfo = this.getZoneInfo();
    if (zoneInfo) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#e0d8c8';
      ctx.fillText(zoneInfo.name, x + size / 2, y - 10);

      ctx.font = '11px Arial';
      ctx.fillStyle = '#999';
      ctx.fillText(zoneInfo.levelRange, x + size / 2, y - 28);
    }

    // --- Legend below map ---
    this.renderLegend(ctx, x, y + size + 12, size);
  }

  private renderLegend(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
    const items: Array<{ shape: 'circle' | 'diamond' | 'star' | 'crystal'; color: string; label: string }> = [
      { shape: 'circle', color: '#cc3333', label: 'Dusman' },
      { shape: 'circle', color: '#ffd700', label: 'NPC' },
      { shape: 'diamond', color: '#a020f0', label: 'Portal' },
      { shape: 'crystal', color: '#64b4ff', label: 'Waypoint' },
      { shape: 'star', color: '#ffd700', label: t('ui.questLabel') },
    ];

    const totalWidth = items.length * 80;
    let startX = x + (width - totalWidth) / 2;

    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    for (const item of items) {
      const iconX = startX + 6;
      const iconY = y + 6;

      ctx.fillStyle = item.color;

      if (item.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(iconX, iconY, 4, 0, Math.PI * 2);
        ctx.fill();
      } else if (item.shape === 'diamond') {
        ctx.beginPath();
        ctx.moveTo(iconX, iconY - 4);
        ctx.lineTo(iconX + 3, iconY);
        ctx.lineTo(iconX, iconY + 4);
        ctx.lineTo(iconX - 3, iconY);
        ctx.closePath();
        ctx.fill();
      } else if (item.shape === 'crystal') {
        // Small crystal shape for waypoints
        ctx.beginPath();
        ctx.moveTo(iconX, iconY - 4);
        ctx.lineTo(iconX + 2.5, iconY - 1);
        ctx.lineTo(iconX + 2.5, iconY + 1);
        ctx.lineTo(iconX, iconY + 3);
        ctx.lineTo(iconX - 2.5, iconY + 1);
        ctx.lineTo(iconX - 2.5, iconY - 1);
        ctx.closePath();
        ctx.fill();
      } else if (item.shape === 'star') {
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
          const outerSX = iconX + Math.cos(angle) * 4;
          const outerSY = iconY + Math.sin(angle) * 4;
          if (i === 0) ctx.moveTo(outerSX, outerSY);
          else ctx.lineTo(outerSX, outerSY);
          const innerAngle = angle + Math.PI / 5;
          ctx.lineTo(iconX + Math.cos(innerAngle) * 2, iconY + Math.sin(innerAngle) * 2);
        }
        ctx.closePath();
        ctx.fill();
      }

      ctx.fillStyle = '#bbb';
      ctx.fillText(item.label, iconX + 8, iconY);

      startX += 80;
    }
  }

  // ===================== SHARED HELPERS =====================

  private renderExpandedEnemies(ctx: CanvasRenderingContext2D, mapX: number, mapY: number, mapSize: number): void {
    if (!this.map) return;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);
    const tileSize = mapSize / Math.max(isoW, isoH);
    const offsetX = (mapSize - isoW * tileSize) / 2;
    const offsetY = (mapSize - isoH * tileSize) / 2;

    for (const enemy of this.enemies) {
      if (!enemy.isAlive) continue;

      const iso = this.worldToIso(enemy.position.x, enemy.position.y);
      const px = mapX + offsetX + (iso.ix + (mapHeight - 1)) * tileSize;
      const py = mapY + offsetY + iso.iy * tileSize;

      const rarity = (enemy as Enemy & { rarity?: EnemyRarity }).rarity || 'normal';
      let dotColor: string;
      let dotSize: number;

      switch (rarity) {
        case 'boss':
          dotColor = '#ff4444';
          dotSize = 6;
          // Pulsing glow
          const pulse = 0.5 + Math.sin(Date.now() / 200) * 0.5;
          ctx.beginPath();
          ctx.arc(px, py, dotSize + 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 68, 68, ${pulse * 0.4})`;
          ctx.fill();
          break;
        case 'elite':
          dotColor = '#ffaa44';
          dotSize = 4;
          break;
        default:
          dotColor = '#cc3333';
          dotSize = 3;
      }

      ctx.beginPath();
      ctx.arc(px, py, dotSize, 0, Math.PI * 2);
      ctx.fillStyle = dotColor;
      ctx.fill();
    }
  }

  private renderSmallMarkers(
    ctx: CanvasRenderingContext2D,
    mapX: number, mapY: number,
    halfSize: number,
    playerIso: { ix: number; iy: number },
    tileSize: number,
    minIsoX: number, maxIsoX: number,
    minIsoY: number, maxIsoY: number
  ): void {
    if (!this.map) return;

    for (const marker of this.markers) {
      const markerIso = this.worldToIso(marker.x, marker.y);

      if (markerIso.ix < minIsoX || markerIso.ix > maxIsoX ||
          markerIso.iy < minIsoY || markerIso.iy > maxIsoY) continue;

      const px = mapX + halfSize + (markerIso.ix - playerIso.ix) * tileSize;
      const py = mapY + halfSize + (markerIso.iy - playerIso.iy) * tileSize;

      this.renderMarkerIcon(ctx, px, py, marker);
    }
  }

  private renderExpandedMarkers(ctx: CanvasRenderingContext2D, mapX: number, mapY: number, mapSize: number): void {
    if (!this.map) return;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);
    const tileSize = mapSize / Math.max(isoW, isoH);
    const offsetX = (mapSize - isoW * tileSize) / 2;
    const offsetY = (mapSize - isoH * tileSize) / 2;

    for (const marker of this.markers) {
      const iso = this.worldToIso(marker.x, marker.y);
      const px = mapX + offsetX + (iso.ix + (mapHeight - 1)) * tileSize;
      const py = mapY + offsetY + iso.iy * tileSize;

      this.renderMarkerIcon(ctx, px, py, marker);
    }
  }

  private renderMarkerIcon(ctx: CanvasRenderingContext2D, x: number, y: number, marker: MinimapMarker): void {
    const time = Date.now() / 1000;
    ctx.save();

    switch (marker.type) {
      case 'npc': {
        // Subtle glow around NPC marker
        const npcGlow = 0.3 + Math.sin(time * 2) * 0.1;
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 215, 0, ${npcGlow * 0.3})`;
        ctx.fill();

        // Yellow dot (3.5px radius) for NPCs
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffd700';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.lineWidth = 0.5;
        ctx.stroke();

        // Quest indicator above NPC with background circle
        if (marker.questMarker === 'available' || marker.questMarker === 'completable') {
          const symbol = marker.questMarker === 'available' ? '!' : '?';
          const qPulse = 0.7 + Math.sin(time * 3) * 0.3;

          // Background circle for quest indicator
          ctx.beginPath();
          ctx.arc(x, y - 9, 5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(50, 40, 0, ${qPulse})`;
          ctx.fill();
          ctx.strokeStyle = `rgba(255, 215, 0, ${qPulse})`;
          ctx.lineWidth = 1;
          ctx.stroke();

          // Symbol
          ctx.font = 'bold 9px Arial';
          ctx.fillStyle = '#ffd700';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(symbol, x, y - 9);
        }
        break;
      }
      case 'portal': {
        // Portal glow
        const portalPulse = 0.5 + Math.sin(time * 2.5) * 0.3;
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(160, 32, 240, ${portalPulse * 0.25})`;
        ctx.fill();

        // Blue-purple diamond (5px) for zone exit points
        ctx.beginPath();
        ctx.moveTo(x, y - 5);
        ctx.lineTo(x + 4, y);
        ctx.lineTo(x, y + 5);
        ctx.lineTo(x - 4, y);
        ctx.closePath();
        ctx.fillStyle = '#a020f0';
        ctx.fill();
        ctx.strokeStyle = 'rgba(180, 100, 255, 0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();
        break;
      }
      case 'quest': {
        // Outer pulsing ring
        const questPulse = 0.4 + Math.sin(time * 4) * 0.35;
        const pulseRadius = 8 + Math.sin(time * 4) * 3;
        ctx.beginPath();
        ctx.arc(x, y, pulseRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 215, 0, ${questPulse * 0.4})`;
        ctx.fill();

        // Second inner pulse ring
        ctx.beginPath();
        ctx.arc(x, y, pulseRadius * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 215, 0, ${questPulse * 0.25})`;
        ctx.fill();

        // Star shape (5 points)
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
          const outerX = x + Math.cos(angle) * 5;
          const outerY = y + Math.sin(angle) * 5;
          if (i === 0) ctx.moveTo(outerX, outerY);
          else ctx.lineTo(outerX, outerY);

          const innerAngle = angle + Math.PI / 5;
          const innerX = x + Math.cos(innerAngle) * 2.2;
          const innerY = y + Math.sin(innerAngle) * 2.2;
          ctx.lineTo(innerX, innerY);
        }
        ctx.closePath();
        ctx.fillStyle = '#ffd700';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 200, 0.5)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
        break;
      }
      case 'waypoint': {
        // Waypoint crystal icon
        if (marker.waypointActivated) {
          // Activated glow ring
          const wpPulse = 0.6 + Math.sin(time * 2) * 0.3;
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(100, 180, 255, ${wpPulse * 0.2})`;
          ctx.fill();

          // Inner glow
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(100, 180, 255, ${wpPulse * 0.4})`;
          ctx.fill();

          // Crystal body (hexagonal-ish shape)
          ctx.beginPath();
          ctx.moveTo(x, y - 6);
          ctx.lineTo(x + 3, y - 2);
          ctx.lineTo(x + 3, y + 2);
          ctx.lineTo(x, y + 4);
          ctx.lineTo(x - 3, y + 2);
          ctx.lineTo(x - 3, y - 2);
          ctx.closePath();
          ctx.fillStyle = '#64b4ff';
          ctx.fill();
          ctx.strokeStyle = 'rgba(180, 220, 255, 0.7)';
          ctx.lineWidth = 1;
          ctx.stroke();

          // Crystal highlight
          ctx.beginPath();
          ctx.moveTo(x - 1, y - 5);
          ctx.lineTo(x + 1, y - 5);
          ctx.lineTo(x + 2, y - 1);
          ctx.lineTo(x - 1, y - 1);
          ctx.closePath();
          ctx.fillStyle = 'rgba(200, 230, 255, 0.5)';
          ctx.fill();
        } else {
          // Inactive - dim grey crystal
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.strokeStyle = '#555';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(x, y - 4);
          ctx.lineTo(x + 2, y - 1);
          ctx.lineTo(x + 2, y + 1);
          ctx.lineTo(x, y + 3);
          ctx.lineTo(x - 2, y + 1);
          ctx.lineTo(x - 2, y - 1);
          ctx.closePath();
          ctx.fillStyle = '#444';
          ctx.fill();
          ctx.strokeStyle = '#555';
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
        break;
      }
    }

    ctx.restore();
  }

  private renderExpandedPlayer(ctx: CanvasRenderingContext2D, mapX: number, mapY: number, mapSize: number): void {
    if (!this.map) return;

    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const isoW = mapWidth + mapHeight - 1;
    const isoH = Math.ceil((mapWidth + mapHeight) / 2);
    const tileSize = mapSize / Math.max(isoW, isoH);
    const offsetX = (mapSize - isoW * tileSize) / 2;
    const offsetY = (mapSize - isoH * tileSize) / 2;

    const iso = this.worldToIso(this.player.position.x, this.player.position.y);
    const px = mapX + offsetX + (iso.ix + (mapHeight - 1)) * tileSize;
    const py = mapY + offsetY + iso.iy * tileSize;

    // Player glow
    ctx.beginPath();
    ctx.arc(px, py, 10, 0, Math.PI * 2);
    const glow = ctx.createRadialGradient(px, py, 0, px, py, 10);
    glow.addColorStop(0, 'rgba(68, 170, 255, 0.5)');
    glow.addColorStop(1, 'rgba(68, 170, 255, 0)');
    ctx.fillStyle = glow;
    ctx.fill();

    // Direction indicator
    const dirAngle = this.player.getFacingAngle();
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + Math.cos(dirAngle) * 12, py + Math.sin(dirAngle) * 12);
    ctx.strokeStyle = '#88ccff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Player dot
    ctx.beginPath();
    ctx.arc(px, py, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#44aaff';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

}
